import { GateOperatingState } from '@/__generated__/graphql';
import { GateDataRenderer } from '@features/gateCommon';
import { translateOperatingState } from '@features/gateCommon/utils/gateOperatingState';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Refresh } from '@mui/icons-material';
import { Box, IconButton, Skeleton } from '@mui/material';
import { type FC } from 'react';
import type { GateLiveStatusState } from '../hooks/useGateLiveStatus';
import { AwaitingDevice } from './AwaitingDevice';
import { DisplayBox } from './DisplayBox';

interface OperatingStatusProps {
  gateId: string | null;
  live: GateLiveStatusState;
}

export const OperatingState: FC<OperatingStatusProps> = ({ gateId, live }) => {
  const { i18n, _ } = useLingui();
  const liveMoving = live.reading?.moving ?? null;

  let value;
  if (!gateId) value = <Trans>Dispositivo no seleccionado</Trans>;
  else if (live.deviceSilent) value = <Trans>Desconectado</Trans>;
  else if (!live.awaiting && liveMoving !== null)
    value = translateOperatingState(
      liveMoving ? GateOperatingState.Operating : GateOperatingState.Idle,
      i18n,
    );
  // Montado también mientras se espera, para que la consulta a la base corra en paralelo; sólo
  // se presenta si el equipo no da el dato.
  else
    value = (
      <GateDataRenderer
        gateId={gateId}
        render={({ operatingState }) =>
          live.awaiting ? <AwaitingDevice /> : translateOperatingState(operatingState, i18n)
        }
        fallback={() => (live.awaiting ? <AwaitingDevice /> : <Skeleton>Estado dummy</Skeleton>)}
      />
    );

  return (
    <DisplayBox label={<Trans>Estado compuerta</Trans>}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {value}
        {gateId && (
          <IconButton
            size="small"
            aria-label={_(msg`Volver a consultar al equipo`)}
            onClick={live.refresh}
            disabled={live.awaiting}
            sx={{ p: '2px', color: 'inherit' }}
          >
            <Refresh sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </DisplayBox>
  );
};
